<template>
  <nav>
    <div
      class="nav-loading-bar"
      :class="{'is-active': navBarVisible, 'is-finishing': isFinishing}"
      :style="{ transform: `scaleX(${navProgress / 100})` }"
    ></div>
    <!-- Desktop Menu & Navbar -->
    <div
      class="w-full fixed top-0 left-0 z-40 transition-all duration-500 ease-out transform border-solid border-0 border-b border-white/20"
      :class="stickyNavClass"
    >
      <div class="header container py-2 lg:py-0" :class="data?.fullWidth ? ' px-4' : 'px-2'">
        <div
          class="flex items-center duration-300 justify-between"
          :class="navContainerClass"
        >
          <!-- Logo -->
          <NuxtLink :to="localePath('/')" class="w-24 lg:w-[120px] flex items-center">
            <img
              v-if="activeLogo"
              :src="`${baseUrl}${activeLogo?.url}`"
              :alt="activeLogo?.alternativeText ?? activeLogo?.hash ?? activeLogo?.caption"
              loading="eager"
              decoding="async"
              width="120"
              height="30"
              class="object-contain"
            />
          </NuxtLink>

          <!-- Header Menu (Desktop) -->
          <ul
            v-if="data?.mainItem && !isMobile"
            class="justify-center gap-2 lg:gap-3 xl:gap-6 2xl:gap-10 hidden lg:flex"
          >
            <template v-for="(item, index) in data?.mainItem">
              <li
                v-if="item.enabled"
                :key="`${index}-${item.id}`"
                class="group py-8"
                 :class="item.subMenuNavigationStyle?.style === 'Small Dropdown Menu' ? 'relative' : ''"
              >
                <NuxtLink
                  :to="localePath(item.link)"
                  :aria-label="item.name"
                  :target="`_${item.target}`"
                  exact-active-class="[&>span]:after:w-full"
                  class="flex items-center group relative gap-2"
                  :class="[isItemActive(item) ? '[&>span]:after:w-[100%] [&>span]:after:left-0 [&>span]:after:right-auto' : '']"
                  ><span
                    class="text-sm xl:text-base font-semibold group-hover:text-opacity-80 duration-200 relative pb-1 after:content-[''] after:w-0 after:h-0.5 after:absolute after:left-auto after:right-0 after:bottom-0 after:duration-500 group-hover:after:w-full group-hover:after:left-0 group-hover:after:right-auto"
                    :class="[isLightHeader ? 'text-black after:bg-black' : 'text-white after:bg-white']"
                    >{{ item.name }}</span
                  >

                  <svg
                    v-if="item.subItem && item.subItem.length > 0"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    class="group-hover:rotate-180 duration-200 group-hover:text-opacity-80"
                    :class="isLightHeader ? 'text-black' : 'text-white'"
                  >
                    <path
                      fill="none"
                      stroke="currentColor"
                      stroke-dasharray="12"
                      stroke-dashoffset="12"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 16l-7 -7M12 16l7 -7"
                    >
                      <animate
                        fill="freeze"
                        attributeName="stroke-dashoffset"
                        dur="0.3s"
                        values="12;0"
                      ></animate>
                    </path>
                  </svg>
                </NuxtLink>
                <template v-if="item.subItem && item.subItem.length > 0">
                  <!-- Mega Menu -->
                  <ul
                    v-if="item.subMenuNavigationStyle?.style === 'Mega Menu'"
                    class="opacity-0 invisible group-hover:opacity-100 group-hover:visible duration-200 absolute left-0 bg-white h-auto w-full p-7 grid gap-5"
                    :class="megaMenuClass"
                  >
                    <template v-for="(subItem, subIndex) in item.subItem">
                      <li
                        v-if="subItem.enabled"
                        :key="`${subIndex}-${subItem.id}`"
                      >
                        <NuxtLink
                          :to="localePath(subItem.link)"
                          :aria-label="subItem.name"
                          :target="`_${subItem.target}`"
                          class="flex"
                        >
                          <div
                            class="bg-brand-background aspect-video overflow-hidden relative"
                            :class="{ 'rounded-lg': siteSettingsStore.roundCorner }"
                          >
                            <AtomsImage
                              :media="subItem.media"
                              :alt="subItemAlt(subItem)"
                              sizeHint="quarter"
                              class="h-full w-full object-cover object-center"
                            />
                            <!-- Base overlay -->
                            <div
                              class="w-full h-full bg-gradient-to-t from-black/60 via-black/60 to-black/60 absolute top-0 left-0"
                            ></div>
                            <!-- Highlight overlay with smooth fade -->
                            <div
                              class="w-full h-full bg-gradient-to-t from-brand-second/50 via-brand-second/50 to-brand-second/50 absolute top-0 left-0 transition-opacity duration-300"
                               :class="isLinkActive(subItem.link) ? 'opacity-100' : 'opacity-0'"
                            ></div>
                            <div
                              class="w-full h-full absolute top-0 left-0 p-5 flex flex-col justify-center items-center text-white"
                            >
                              <div
                                v-if="subItem.name"
                                class="text-lg xl:text-[22px] font-semibold text-center"
                              >
                                {{ subItem.name }}
                              </div>
                              <div
                                v-if="subItem.description"
                                class="font-normal text-xs xl:text-sm mt-1 xl:mt-3 text-center"
                                v-html="subItem.description"
                              ></div>
                            </div>
                          </div>
                        </NuxtLink>
                      </li>
                    </template>
                  </ul>

                  <!-- Link Menu -->
                  <div
                    v-else-if="
                      item.subMenuNavigationStyle?.style === 'Link Menu'
                    "
                    class="opacity-0 invisible group-hover:opacity-100 group-hover:visible duration-200 absolute left-0 bg-white h-auto w-full p-7 flex"
                    :class="[
                      data?.fullWidth ? 'shadow-md ' : 'shadow-medium ',
                      siteSettingsStore.roundCorner
                        ? 'rounded-b-lg top-[calc(100%-8px)]'
                        : 'top-full',
                    ]"
                  >
                    <div class="w-3/4 flex flex-wrap gap-y-6 xl:gap-y-8">
                      <ul
                        v-for="colIndex in Math.ceil(item.subItem.length / (data?.fullWidth ? 4 : 5))"
                        :key="colIndex"
                        :class="[data?.fullWidth ? 'w-1/4 flex flex-col gap-2 xl:gap-3' : 'w-1/3 flex flex-col gap-2 xl:gap-3', data?.fullWidth ? ((colIndex - 1) % 4 !== 0 ? 'pl-5' : '') : ((colIndex - 1) % 3 !== 0 ? 'pl-5' : '')]"
                      >
                        <li
                          v-for="subItem in item.subItem
                            .slice((colIndex - 1) * 5, colIndex * 5)
                            .filter((s) => s.enabled)"
                          :key="subItem.id"
                          :class="{ 'rounded-lg': siteSettingsStore.roundCorner }"
                        >
                          <NuxtLink
                            :to="localePath(subItem.link)"
                            :aria-label="subItem.name"
                            :target="`_${subItem.target}`"
                            class="flex font-semibold text-black duration-200 hover:text-brand-second w-fit text-sm xl:text-base"
                            :class="{'text-brand-second': isItemActive(subItem)}"
                          >
                            {{ subItem.name }}
                          </NuxtLink>
                        </li>
                      </ul>
                    </div>

                    <div
                      class="w-1/4 pl-6 flex items-start justify-end"
                      v-if="item.subMenuNavigationStyle?.menuBanner"
                    >
                      <div class="w-full h-auto 2xl:w-72">
                        <AtomsImage
                          :src="item.subMenuNavigationStyle?.menuBanner.formats?.thumbnail?.url ?? item.subMenuNavigationStyle?.menuBanner.url"
                          :alt="item.subMenuNavigationStyle?.alternativeText ?? item.subMenuNavigationStyle?.hash ?? item.subMenuNavigationStyle?.caption"
                          class="h-full w-full object-cover object-center"
                          :class="{'rounded-lg': siteSettingsStore.roundCorner}"
                        />
                      </div>
                    </div>
                  </div>

                  <!-- Small Dropdown Menu -->
                  <div
                    v-else-if="
                      item.subMenuNavigationStyle?.style ===
                      'Small Dropdown Menu'
                    "
                    class="opacity-0 invisible group-hover:opacity-100 group-hover:visible duration-200 absolute top-[calc(100%-12px)] w-48 bg-white border border-gray-100 shadow-xl shadow-black/5 p-2"
                    :class="[siteSettingsStore.roundCorner ? 'rounded-xl ' : '']"
                  >
                    <ul class="flex flex-col gap-1 w-full relative z-10">
                      <template v-for="(subItem, subIndex) in item.subItem">
                        <li
                          v-if="subItem.enabled"
                          :key="`${subIndex}-${subItem.id}`"
                          class="w-full"
                        >
                          <NuxtLink
                            :to="localePath(subItem.link)"
                            :aria-label="subItem.name"
                            :target="`_${subItem.target}`"
                            class="flex font-medium text-sm text-gray-700 hover:text-black hover:bg-gray-50 duration-200 w-full px-4 py-2.5 rounded-md"
                            exact-active-class="text-brand-second bg-brand-second/5 font-semibold"
                            :class="{
                              'text-brand-second bg-brand-second/5 font-semibold': isLinkActive(subItem.link),
                            }"
                            >{{ subItem.name }}
                          </NuxtLink>
                        </li>
                      </template>
                    </ul>
                  </div>

                  <!-- Thumbnail Menu -->
                  <ul
                    v-else
                    class="opacity-0 invisible group-hover:opacity-100 group-hover:visible duration-200 absolute left-0 bg-white h-auto w-full p-7 flex flex-wrap"
                    :class="[
                      data?.fullWidth ? 'shadow-md ' : 'shadow-medium ',
                      siteSettingsStore.roundCorner
                        ? 'rounded-b-lg top-[calc(100%-8px)]'
                        : 'top-full',
                    ]"
                  >
                    <template v-for="(subItem, subIndex) in item.subItem">
                      <li
                        v-if="subItem.enabled"
                        :key="`${subIndex}-${subItem.id}`"
                        class="p-3 w-1/2 xl:w-1/5 duration-200 hover:shadow-medium"
                        :class="[{ 'rounded-lg': siteSettingsStore.roundCorner }]"
                      >
                        <NuxtLink
                          :to="localePath(subItem.link)"
                          :aria-label="subItem.name"
                          :target="`_${subItem.target}`"
                          class="flex"
                        >
                          <div
                            class="bg-brand-background p-1 w-[70px] h-[85px]"
                            :class="{ 'rounded-lg': siteSettingsStore.roundCorner }"
                          >
                            <AtomsImage
                              :src="
                                subItem.media?.formats?.thumbnail?.url ??
                                subItem.media?.url
                              "
                              :alt="
                                subItem.media?.alternativeText ??
                                subItem.media?.hash ??
                                subItem.media?.caption
                              "
                              class="h-full w-full object-cover object-center"
                              :class="{
                                'rounded-lg': siteSettingsStore.roundCorner,
                              }"
                            />
                          </div>
                          <div
                            class="w-[calc(100%-70px)] py-2 px-3 flex flex-col justify-center"
                          >
                            <div
                              v-if="subItem.name"
                              class="font-semibold text-black"
                              :class="{
                                'text-brand-second': isItemActive(subItem),
                              }"
                            >
                              {{ subItem.name }}
                            </div>
                            <div
                              v-if="subItem.description"
                              class="font-normal text-sm"
                              v-html="subItem.description"
                            ></div>
                          </div>
                        </NuxtLink>
                      </li>
                    </template>
                  </ul>
                </template>
              </li>
            </template>
          </ul>

          <!-- Language Button (Desktop) -->
          <div
            v-if="!isMobile"
            class="items-center gap-3 hidden lg:flex justify-end"
          >
            <AtomsButton
              v-if="siteSetting?.headerButton?.enabled"
              :label="siteSetting?.headerButton?.label"
              :link="siteSetting?.headerButton?.link"
              :color="siteSetting?.headerButton?.color"
              :icon="siteSetting?.headerButton?.icon"
              :iconPosition="siteSetting?.headerButton?.iconPosition"
              :size="siteSetting?.headerButton?.size"
              :target="siteSetting?.headerButton?.target"
              :disabled="siteSetting?.headerButton?.disabled"
              class="hidden xl:flex !py-2 !px-3"
            />
            <div
              class="flex gap-2"
              :class="{'xl:pl-3 xl:border-l xl:border-solid xl:border-gray-200 xl:border-r-0 xl:border-t-0 xl:border-b-0': siteSetting?.headerButton?.enabled}"
            >
              <button
                type="button"
                :aria-label="t('Search.Title')"
                class="cursor-pointer w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-black hover:text-brand-second hover:border-brand-second duration-200 rounded-full"
                @click="openSearch"
              >
                <i class="pi pi-search text-base"></i>
              </button>
              <div
                v-if="langSwitcherEnabled && siteSetting?.language"
                @click="modalOpen()"
                class="w-10 h-10 cursor-pointer bg-gray-100 hover:bg-gray-200 rounded-full flex justify-center items-center"
              >
                <span
                :class="`rounded-sm fi fi-${locale === 'tr' ? 'tr' : (locale === 'ar' || locale === 'sa') ? 'sa' : locale === 'de' ? 'de' : locale === 'es' ? 'es' : locale === 'fr' ? 'fr' : 'gb'}`"
                ></span>
              </div>

              <!-- <AtomsButton
                v-if="siteSetting?.language"
                @click="modalOpen()"
                :label="locale"
                color="second"
                :enabled="true"
                class="!w-10 !h-10 !rounded-full !p-0"
              /> -->
            </div>
          </div>

          <!-- Hamburger Menu Button (Mobile) -->
          <div v-if="isMobile" class="flex items-center gap-3">
            <button
              type="button"
              :aria-label="t('Search.Title')"
              class="cursor-pointer w-10 h-10 flex items-center justify-center duration-200 rounded-full"
                :class="isLightHeader ? 'bg-gray-100 text-black hover:text-brand-second' : 'bg-white/15 text-white'"
              @click="openSearch"
            >
              <i class="pi pi-search text-base"></i>
            </button>
            <button
              v-if="langSwitcherEnabled && siteSetting?.language"
              type="button"
              aria-label="language"
              class="w-10 h-10 cursor-pointer rounded-full flex justify-center items-center duration-200"
              :class="
                isLightHeader
                  ? 'bg-gray-100'
                  : 'bg-white/15 border border-white/20'
              "
              @click="modalOpen()"
            >
              <span :class="langIconClass"></span>
            </button>
            <button
              @click="toggleMenu"
              aria-label="menu"
              :aria-expanded="isOpen"
              aria-controls="mobile-menu"
              class="relative w-8 h-8 flex flex-col justify-center items-center gap-1 z-50 cursor-pointer"
            >
              <span
                :class="[
                  'w-8 h-[3px] absolute transition-all duration-200 ease-in-out rounded-full',
                  isLightHeader ? 'bg-brand-dark' : 'bg-white',
                  isOpen ? 'rotate-45 top-1/2' : '-translate-y-2',
                ]"
              ></span>
              <span
                :class="[
                  'w-8 h-[3px] transition-all duration-200 ease-in-out rounded-full',
                  isLightHeader ? 'bg-brand-dark' : 'bg-white',
                  isOpen ? 'opacity-0' : 'opacity-100',
                ]"
              ></span>
              <span
                :class="[
                  'w-8 h-[3px] absolute transition-all duration-200 ease-in-out rounded-full',
                  isLightHeader ? 'bg-brand-dark' : 'bg-white',
                  isOpen ? '-rotate-45 top-1/2' : 'translate-y-2',
                ]"
              ></span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Mobile Menu -->
    <Transition name="fade">
      <div
        v-if="isMobile && isOpen"
        id="mobile-menu"
        class="bg-white fixed top-0 left-0 h-svh w-full z-30 overflow-auto"
      >
        <div class="container">
          <div class="w-full pt-16">
            <ul v-if="data?.mainItem" class="w-full flex flex-col pb-[160px]">
              <template v-for="(item, index) in data?.mainItem">
                <li
                  v-if="item.enabled"
                  :key="`${index}-${item.id}`"
                  class="group py-3 px-5 lg:px-0"
                >
                  <div
                    class="flex items-center relative w-full justify-between"
                  >
                    <NuxtLink
                      :to="localePath(item.link)"
                      :aria-label="item.name"
                      :target="`_${item.target}`"
                      exact-active-class="text-brand-second"
                      :class="['text-lg font-semibold text-black duration-200', activeIndex === index || isItemActive(item) ? 'text-brand-second' : '']"
                      >{{ item.name }}</NuxtLink
                    >
                    <svg
                      v-if="item.subItem && item.subItem.length > 0"
                      @click="item.subItem && item.subItem.length > 0 ? toggleDropdown(index) : null"
                      role="button"
                      tabindex="0"
                      aria-label="Toggle submenu"
                      :aria-expanded="activeIndex === index"
                      :aria-controls="`mobile-submenu-${index}`"
                      @keydown.enter.prevent="toggleDropdown(index)"
                      @keydown.space.prevent="toggleDropdown(index)"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      class="text-black duration-200"
                      :class="[activeIndex === index ? 'rotate-180 text-brand-second' : '', isItemActive(item) ? 'text-brand-second' : '']"
                    >
                      <path
                        fill="none"
                        stroke="currentColor"
                        stroke-dasharray="12"
                        stroke-dashoffset="12"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 16l-7 -7M12 16l7 -7"
                      >
                        <animate
                          fill="freeze"
                          attributeName="stroke-dashoffset"
                          dur="0.3s"
                          values="12;0"
                        ></animate>
                      </path>
                    </svg>
                  </div>
                  <ul
                    v-if="item.subItem && item.subItem.length > 0 && activeIndex === index"
                    :id="`mobile-submenu-${index}`"
                    class="pt-2"
                  >
                    <template v-for="(subItem, subIndex) in item.subItem">
                      <li
                        v-if="subItem.enabled"
                        :key="`${subIndex}-${subItem.id}`"
                        class="group py-2 last:pt-2 last:pb-0"
                      >
                        <NuxtLink
                          :to="localePath(subItem.link)"
                          :aria-label="subItem.name"
                          :target="`_${subItem.target}`"
                          class="text-base text-black hover:text-opacity-70 duration-200 flex w-full"
                          :class="{
                            'text-brand-second': isItemActive(subItem),
                          }"
                          >{{ subItem.name }}</NuxtLink
                        >
                      </li>
                    </template>
                  </ul>
                </li>
              </template>
            </ul>
            <div
              v-if="siteSetting?.phone || siteSetting?.headerButton?.enabled"
              class="flex flex-col fixed bottom-0 left-0 w-full bg-white z-20"
              :class="{ 'rounded-t-lg': siteSettingsStore.roundCorner }"
            >
              <div v-if="siteSetting?.headerButton?.enabled" class="px-5 pt-3 pb-3 w-full border-t border-gray-100">
                <AtomsButton
                  :label="siteSetting?.headerButton?.label"
                  :link="siteSetting?.headerButton?.link"
                  :color="siteSetting?.headerButton?.color"
                  :icon="siteSetting?.headerButton?.icon"
                  :iconPosition="siteSetting?.headerButton?.iconPosition"
                  :size="siteSetting?.headerButton?.size"
                  :target="siteSetting?.headerButton?.target"
                  :disabled="siteSetting?.headerButton?.disabled"
                  class="w-full flex justify-center !py-3 !text-base"
                />
              </div>
              <div
                v-if="siteSetting?.phone"
                class="flex py-2.5 bg-brand-dark w-full px-5"
              >
                <div class="w-full flex items-center justify-center gap-4">
                  <a
                    :href="telHref(siteSetting?.phone)"
                    class="flex items-center justify-center"
                  >
                    <svg
                      fill="#ffffff"
                      width="40px"
                      height="40px"
                      viewBox="0 0 56 56"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                       <path
                        d="M 27.9999 51.9063 C 41.0546 51.9063 51.9063 41.0781 51.9063 28 C 51.9063 14.9453 41.0312 4.0937 27.9765 4.0937 C 14.8983 4.0937 4.0937 14.9453 4.0937 28 C 4.0937 41.0781 14.9218 51.9063 27.9999 51.9063 Z M 27.9999 47.9219 C 16.9374 47.9219 8.1014 39.0625 8.1014 28 C 8.1014 16.9609 16.9140 8.0781 27.9765 8.0781 C 39.0155 8.0781 47.8983 16.9609 47.9219 28 C 47.9454 39.0625 39.0390 47.9219 27.9999 47.9219 Z M 22.3749 33.5312 C 27.8358 39.0156 34.7968 42.4844 38.9218 38.3828 C 39.0858 38.2188 39.2030 38.0781 39.3202 37.9141 C 40.3983 36.7188 40.7030 35.1250 39.3202 34.0937 C 38.2421 33.3203 37.0936 32.5234 35.1483 31.1641 C 33.7890 30.2031 32.8983 30.4375 31.8436 31.4922 L 30.9062 32.4063 C 30.6249 32.6641 30.1796 32.6406 29.8749 32.4531 C 29.0546 31.9609 27.6718 30.8594 26.3593 29.5469 C 25.0702 28.2578 23.8983 26.8047 23.4530 26.0312 C 23.2890 25.7734 23.1718 25.3516 23.4999 25.0469 L 24.4140 24.0625 C 25.4687 22.9609 25.7030 22.1172 24.7187 20.7344 L 21.8358 16.6563 C 20.8514 15.2734 19.4218 15.5312 17.8514 16.7031 C 17.7343 16.7968 17.6405 16.8906 17.5468 16.9844 C 13.4218 21.1094 16.9140 28.0703 22.3749 33.5312 Z"
                      />
                    </svg>
                    <div class="font-saira text-white font-medium text-sm pl-1">
                      {{ siteSetting?.phone }}
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <SearchModal
      v-if="searchModalMounted"
      :visible="searchModalVisible"
      @update:visible="searchModalVisible = $event"
    />
    <!-- Language Modal -->
    <LanguageModal
      v-if="languageModalMounted"
      :visible="modalLangVisible"
      @update:visible="modalLangVisible = $event"
    />
  </nav>
</template>
<script setup>
import { defineAsyncComponent, onBeforeUnmount, watch, computed, ref } from "vue";
import { useLocalePath } from "#i18n";
import { useRoute } from "vue-router";
import { useIsMobile } from "~/composables/utils";
import { useI18n } from "vue-i18n";
import { useLoadingStore, useSiteSettingsStore } from "@/store/index";

const { locale, setLocale, t } = useI18n();
const localePath = useLocalePath();
const route = useRoute();
const { isMobile } = useIsMobile();
const loadingStore = useLoadingStore();
const siteSettingsStore = useSiteSettingsStore();
const navLoading = computed(() => loadingStore.isNavLoading);
const navProgress = ref(0);
const isFinishing = ref(false);
const navBarVisible = computed(() => navLoading.value || isFinishing.value);
let progressTimer = null;
let finishTimer = null;

const clearProgressTimer = () => {
  if (progressTimer) {
    clearInterval(progressTimer);
    progressTimer = null;
  }
};

const clearFinishTimer = () => {
  if (finishTimer) {
    clearTimeout(finishTimer);
    finishTimer = null;
  }
};

watch(
  navLoading,
  (val) => {
    if (val) {
      isFinishing.value = false;
      clearFinishTimer();
      clearProgressTimer();
      navProgress.value = 0;
      // Slowly ramp up to 95% while loading continues
      progressTimer = setInterval(() => {
        navProgress.value = Math.min(navProgress.value + 2.5, 95);
      }, 80);
      return;
    }

    // Let the bar reach 100% before hiding
    isFinishing.value = true;
    clearProgressTimer();
    navProgress.value = 100;
    finishTimer = setTimeout(() => {
      isFinishing.value = false;
      navProgress.value = 0;
      finishTimer = null;
    }, 350);
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  clearProgressTimer();
  clearFinishTimer();
});
const isHome = computed(() => {
  // localePath("/") => "/" (tr), "/en", "/ar" ... — locale önekini hesaba kat
  const home = localePath("/");
  const strip = (x) => ((x && x !== "/" && x.endsWith("/")) ? x.slice(0, -1) : x) || "/";
  return strip(route.path) === strip(home);
});
const isScrolled = ref(false);
const isOpen = ref(false);
const activeIndex = ref(null);

const props = defineProps({
  data: {
    type: Object,
  },
  siteSetting: {
    type: Object,
  },
});

const runtimeConfig = useRuntimeConfig();
const baseUrl = runtimeConfig.public.baseUrl;
const isLightHeader = computed(
  () => isScrolled.value || !isHome.value || (isMobile.value && isOpen.value),
);
const headerLogos = computed(() => {
  const raw = props.siteSetting?.headerLogo;
  if (Array.isArray(raw)) return raw;
  if (raw) return [raw];
  return [];
});

const activeLogo = computed(() => {
  const logos = headerLogos.value;
  // index 0 = sticky/light logo, index 1 = normal/dark logo
  if (isLightHeader.value) {
    return logos[0] ?? logos[1] ?? null;
  }
  return logos[1] ?? logos[0] ?? null;
});

// Dil değiştirici (bayrak butonu). İçerik EN/AR'a çevrildiği için aktif.
const langSwitcherEnabled = true;

const modalLangVisible = ref(false);
const { mounted: languageModalMounted } = useLazyMount(modalLangVisible, 250);
const langIconClass = computed(() => {
  const code = locale.value === "tr" ? "tr" : (locale.value === "ar" || locale.value === "sa") ? "sa" : locale.value === "de" ? "de" : locale.value === "es" ? "es" : locale.value === "fr" ? "fr" : "gb";
  return `rounded-sm fi fi-${code}`;
});
const LanguageModal = defineAsyncComponent(
  () => import("~/components/Plugins/Modal/Language.vue"),
);
const searchModalVisible = ref(false);
const { mounted: searchModalMounted } = useLazyMount(searchModalVisible, 250);
const SearchModal = defineAsyncComponent(
  () => import("~/components/Plugins/Modal/Search.vue"),
);

function modalOpen() {
  modalLangVisible.value = true;
}

function openSearch() {
  searchModalVisible.value = true;
  if (isMobile && isOpen.value) {
    isOpen.value = false;
  }
}

// Hamburger Menu toggle
const toggleMenu = () => {
  isOpen.value = !isOpen.value;
};

// Hamurger Menu Children Linkler
const toggleDropdown = (index) => {
  activeIndex.value = activeIndex.value === index ? null : index;
};

// Navigation Bar Arkaplan Beyaz Olması Durumu
const navBackground = computed(() => {
  if (isMobile && isOpen.value) {
    return isHome.value
      ? "bg-white lg:bg-transparent lg:shadow-none"
      : "bg-white";
  }

  if (isHome.value) {
    return isScrolled.value
      ? "bg-white shadow-sm"
      : "bg-transparent shadow-none";
  } else {
    return "bg-white shadow-sm";
  }
});

const navSpacingClass = computed(() => {
  if (!props.data?.fullWidth) {
    return isScrolled.value ? "py-2 lg:py-3" : "py-2 lg:py-5";
  }
});

const normalizePath = (path) => {
  if (!path) return "/";
  if (path !== "/" && path.endsWith("/")) {
    return path.slice(0, -1);
  }
  return path || "/";
};

const isLinkActive = (link) => {
  const targetPath = normalizePath(localePath(link));
  const currentPath = normalizePath(route.path);

  if (targetPath === "/") {
    return currentPath === "/";
  }

  return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
};

const isItemActive = (item) => {
  if (!item) return false;
  if (isLinkActive(item.link)) return true;

  return (
    Array.isArray(item.subItem) &&
    item.subItem.some((subItem) => isLinkActive(subItem.link))
  );
};

const handleScroll = () => {
  isScrolled.value = window.scrollY > 0;
};

onMounted(() => {
  window.addEventListener("scroll", handleScroll, { passive: true });
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
  clearProgressTimer();
  clearFinishTimer();
});

watchEffect(() => {
  const logo = activeLogo.value;
  if (!logo?.url) return;

  const preloadLinks = [{
    rel: "preload",
    as: "image",
    href: `${baseUrl}${logo.url}`,
  }];

  useHead({
    link: [
      ...preloadLinks,
      {
        rel: "preconnect",
        href: baseUrl,
        crossorigin: true,
      },
      {
        rel: "dns-prefetch",
        href: baseUrl,
      },
    ],
  });
});

// Menü açılıp kapanınca body overflow kontrolü
watchEffect(() => {
  if (!process.client) return;

  if (isOpen.value || searchModalVisible.value) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
    activeIndex.value = null;
  }
});

// Sayfa değiştiğinde menüyü kapat ve scroll'u aç
watch(
  () => route.path,
  async () => {
    if (!process.client) return;
    isOpen.value = false;
    searchModalVisible.value = false;
    await nextTick();
    document.body.style.overflow = "";
    activeIndex.value = null;
  },
);
const stickyNavClass = computed(() => [
  navSpacingClass.value,
  navBackground.value,
  {
    'bg-white shadow-none': isOpen.value && isMobile.value,
    'w-full': props.data?.fullWidth,
    'w-auto': !props.data?.fullWidth,
  },
  { 'rounded-xl': !props.data?.fullWidth && siteSettingsStore.roundCorner },
]);
const navContainerClass = computed(() => [
  props.data?.fullWidth ? '' : 'relative shadow-soft px-4 lg:px-6 xl:px-8',
  { 'rounded-lg': siteSettingsStore.roundCorner },
  { '!shadow-none': isOpen.value },
]);
const megaMenuClass = computed(() => [
  props.data?.fullWidth ? 'shadow-md grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5' : 'shadow-medium grid-cols-4',
  siteSettingsStore.roundCorner ? 'rounded-b-lg top-[calc(100%-8px)]' : 'top-full',
]);
const subItemAlt = (subItem) => subItem.media?.alternativeText ?? subItem.media?.hash ?? subItem.media?.caption;
</script>
<style>
.header {
  @apply w-full mx-auto;
}

.nav-loading-bar {
  position: fixed;
  inset: 0 0 auto 0;
  height: 3px;
  background: linear-gradient(90deg, #fc4845, #ff6d6a);
  transform-origin: left;
  transform: scaleX(0);
  opacity: 0;
  transition:
    transform 0.35s ease,
    opacity 0.25s ease;
  z-index: 50;
  pointer-events: none;
}
.nav-loading-bar.is-active {
  opacity: 1;
}
.nav-loading-bar.is-finishing {
  transform: scaleX(1);
  opacity: 1;
}
</style>
